<template>
  <div
    class="p-space-md d-flex flex-column"
    :class="cardClass"
    :style="cardStyle"
    @click="handleCardClick"
    @click.middle="handleCardClick"
    @keyup.enter="handleCardEnter"
    :tabindex="cardTabIndex">
    <div :class="imageClass" class="mb-space-sm mb-lg-space-md">
      <img
        class="icon-lazy"
        :data-src="iconSource"
        :alt="iconAlt"
        :style="iconStyle" />
    </div>
    <div class="p-0">
      <h3
        v-if="displayTitle"
        class="mb-space-sm mb-md-space-md"
        :class="titleClass"
        v-html="title"></h3>

      <!-- slot -->
      <div>
        <slot />
      </div>
    </div>
    <div
      v-if="displayCta"
      class="card-footer-border p-0 flex-grow-1 d-flex align-items-end">
      <div class="row">
        <div class="col-12" :class="ctaAlignClass">
          <a
            :target="ctaLinkTarget"
            :class="ctaClass"
            :href="ctaLink"
            :aria-label="ctaText"
            @click.stop="handleClick"
            class="cta-button">
            {{ ctaText }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import lozad from "lozad";
import { computed, onMounted } from "vue";

interface Props {
  displayBorder?: boolean;
  borderVariant?: string;
  cardBackgroundVariant?: string;
  iconSource: string;
  iconAlt: string;
  iconAlign?: "left" | "right" | "center";
  iconHeight?: number;
  displayTitle?: boolean;
  title?: string;
  titleSize?: string;
  titleVariant?: string;
  titleAlign?: "left" | "right" | "center";
  displayCta?: boolean;
  ctaAsButton?: boolean;
  ctaButtonVariant?: string;
  ctaText?: string;
  ctaTextSize?: string;
  ctaTextVariant?: string;
  ctaLink?: string;
  ctaTextAlign?: "left" | "right" | "center";
  ctaTextWeight?: string;
  ctaLinkTarget?: "_self" | "_blank" | "_parent" | "_top";
  clickableCard?: boolean;
  highlightLeft?: boolean;
  highlightColor?: string;
  highlightWidth?: number;
}

const props = withDefaults(defineProps<Props>(), {
  displayBorder: true,
  borderVariant: "light-4",
  cardBackgroundVariant: "white",
  iconAlign: "left",
  iconHeight: 48,
  displayTitle: true,
  title: "Title",
  titleSize: "large",
  titleVariant: "dark-3",
  titleAlign: "left",
  displayCta: true,
  ctaAsButton: false,
  ctaButtonVariant: "primary",
  ctaText: "Lorem Ipsum",
  ctaTextSize: "medium",
  ctaTextVariant: "primary",
  ctaLink: "https://asuonline.asu.edu/",
  ctaTextAlign: "left",
  ctaTextWeight: "normal",
  ctaLinkTarget: "_self",
  clickableCard: false,
  highlightLeft: false,
  highlightColor: "var( --rds-secondary, #ffc627)",
  highlightWidth: 8,
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when any part of the card is clicked except for cta click.
   */
  (e: "cardClick", event: Event): void;
  /**
   *  Emitted when the cta is clicked, if present.
   */
  (e: "ctaClick", event: Event): void;
  /**
   *  Emitted when any the enter key is pressed while the card has focus and clickable-card is set to true.
   */
  (e: "cardEnter", event: Event): void;
}>();

const getColor = (color: string) => {
  if (color.includes("var(")) {
    return `${color}`;
  } else if (color.includes("--")) {
    return `var(${color})`;
  } else {
    return color;
  }
};

const ctaAlignMap = {
  left: "start",
  center: "center",
  right: "end",
};

const cardTabIndex = computed(() => {
  return props.clickableCard ? "0" : "-1";
});

const cardStyle = computed(() => {
  let style = "";
  style += `cursor:${props.clickableCard ? "pointer" : "default"};`;
  if (props.highlightLeft) {
    style += `border-left:${props.highlightWidth}px solid ${getColor(
      props.highlightColor
    )} !important`;
  }
  return style;
});

const cardClass = computed(() => {
  let classNames = `bg-${props.cardBackgroundVariant}`;

  if (props.displayBorder) {
    classNames += ` border border-${props.borderVariant} `;
  } else {
    classNames += " border-0";
  }

  if (props.clickableCard) {
    classNames += " card-focus";
  }

  return classNames;
});

const imageClass = computed(() => {
  return `text-${ctaAlignMap[props.iconAlign]}`;
});

const iconStyle = computed(() => {
  return {
    height: `${props.iconHeight}px`,
  };
});

const titleClass = computed(() => {
  return `h3-${props.titleSize} text-${props.titleVariant} text-${
    ctaAlignMap[props.titleAlign]
  }`;
});

const ctaAlignClass = computed(() => {
  return `text-${ctaAlignMap[props.ctaTextAlign]}`;
});

const ctaClass = computed(() => {
  let classNames = "";

  if (props.ctaAsButton) {
    classNames += `px-space-xs py-space-xxs btn btn-${props.ctaButtonVariant} `;
  } else {
    classNames += "rds-link ";
  }
  classNames += `fs-${props.ctaTextSize} text-${props.ctaTextVariant} fw-${props.ctaTextWeight}`;

  return classNames;
});

const handleCardClick = (event: Event) => {
  emits("cardClick", event);
};
const handleClick = (event: Event) => {
  emits("ctaClick", event);
};
const handleCardEnter = (event: Event) => {
  if (props.clickableCard) {
    emits("cardEnter", event);
  }
};

onMounted(() => {
  if (typeof window !== "undefined" && window !== null) {
    const observer = lozad(".icon-lazy");
    observer.observe();
  }
});
</script>

<style lang="scss" scoped>
.card-footer-border {
  border-top: none;
}
.cta-button {
  text-decoration: none;
}
</style>
